/**
 * Collection Targets API service
 *
 * Provides functions for managing collection targets, bulk status updates,
 * schedule CSV import/export, re-collection, and endpoint collection history.
 */

import { apiClient } from './api'

import { API_BASE_URL } from './api-base-url'
import type {
  CollectionTarget,
  CollectionTargetSummary,
  BulkStatusUpdateInput,
  BulkStatusUpdateResult,
  ListTargetsParams,
  EndpointCollectionHistory,
  ScheduleImportResult,
  RecollectInput,
  CreateEnhancedCollectionRequestInput,
  EnhancedCreateResponse,
  CollectionMonitoringResponse,
  MonitoringFilterParams,
} from '../types/collection-targets'

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Build query string from params object
 */
function buildQueryString(params: object): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// =============================================================================
// COLLECTION TARGETS API
// =============================================================================

/**
 * List targets for a collection request
 */
export async function listTargets(
  caseId: string,
  requestId: string,
  params?: ListTargetsParams
): Promise<CollectionTarget[]> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<CollectionTarget[]>(
    `/cases/${caseId}/collection-requests/${requestId}/targets${query}`
  )
}

/**
 * Bulk update target statuses
 */
export async function bulkUpdateStatus(
  caseId: string,
  requestId: string,
  input: BulkStatusUpdateInput
): Promise<BulkStatusUpdateResult> {
  return apiClient.post<BulkStatusUpdateResult>(
    `/cases/${caseId}/collection-requests/${requestId}/targets/bulk-status`,
    input
  )
}

/**
 * Get target dashboard summary for a collection request
 */
export async function getTargetSummary(
  caseId: string,
  requestId: string
): Promise<CollectionTargetSummary> {
  return apiClient.get<CollectionTargetSummary>(
    `/cases/${caseId}/collection-requests/${requestId}/targets/summary`
  )
}

/**
 * Get collection history for an endpoint
 */
export async function getEndpointCollectionHistory(
  caseId: string,
  endpointId: string
): Promise<EndpointCollectionHistory> {
  return apiClient.get<EndpointCollectionHistory>(
    `/cases/${caseId}/endpoints/${endpointId}/collection-history`
  )
}

// =============================================================================
// SCHEDULE API
// =============================================================================

/**
 * Download schedule CSV for a collection request
 */
export async function downloadSchedule(caseId: string, requestId: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/cases/${caseId}/collection-requests/${requestId}/schedule`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('evidenx_access_token') ?? ''}`,
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to download schedule: ${response.statusText}`)
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `schedule-${requestId}.csv`
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

/**
 * Import schedule CSV for a collection request
 */
export async function importSchedule(
  caseId: string,
  requestId: string,
  file: File
): Promise<ScheduleImportResult> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(
    `${API_BASE_URL}/cases/${caseId}/collection-requests/${requestId}/schedule/import`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('evidenx_access_token') ?? ''}`,
      },
      body: formData,
    }
  )

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null
    throw new Error(body?.message ?? `Failed to import schedule: ${response.statusText}`)
  }

  return response.json() as Promise<ScheduleImportResult>
}

// =============================================================================
// COLLECTION MONITORING API
// =============================================================================

/**
 * Get case-wide collection monitoring data with summary statistics
 */
export async function getCollectionMonitoring(
  caseId: string,
  params?: MonitoringFilterParams
): Promise<CollectionMonitoringResponse> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<CollectionMonitoringResponse>(
    `/cases/${caseId}/collection-monitoring${query}`
  )
}

// =============================================================================
// ENHANCED COLLECTION REQUEST API
// =============================================================================

/**
 * Create an enhanced collection request with artefact template integration
 */
export async function createEnhancedCollectionRequest(
  caseId: string,
  input: CreateEnhancedCollectionRequestInput
): Promise<EnhancedCreateResponse> {
  return apiClient.post<EnhancedCreateResponse>(
    `/cases/${caseId}/collection-requests/enhanced`,
    input
  )
}

/**
 * Create a re-collection request from failed/partial targets
 */
export async function recollect(
  caseId: string,
  requestId: string,
  input: RecollectInput
): Promise<EnhancedCreateResponse> {
  return apiClient.post<EnhancedCreateResponse>(
    `/cases/${caseId}/collection-requests/${requestId}/recollect`,
    input
  )
}
